import { Navigate } from 'react-router-dom';
import useAuthRole from '@hooks/useAuthRole';

/**
 * ProtectedRoute - Component that wraps routes requiring specific roles
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {string[]} props.allowedRoles - Array of roles allowed to access this route
 * @param {string} props.redirectPath - Path to redirect if unauthorized (default: '/unauthorized')
 *
 * @returns {React.ReactNode} - Children if authorized, Navigate component if not
 *
 * @example
 * // Route accessible only to ADMIN and MANAGER roles
 * <ProtectedRoute allowedRoles={[ROLES.ADMIN_ROLE, ROLES.MANAGER_ROLE]}>
 *   <AdminPanel />
 * </ProtectedRoute>
 *
 * @example
 * // Route accessible to all authenticated users (no role restriction)
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 */
const ProtectedRoute = ({
  children,
  allowedRoles = null,
  redirectPath = '/unauthorized'
}) => {
  const { hasAnyAuthority } = useAuthRole();

  // If no allowedRoles specified, route is accessible to all authenticated users
  if (!allowedRoles || allowedRoles.length === 0) {
    return children;
  }

  // Check if user has any of the required roles
  const hasAccess = hasAnyAuthority(...allowedRoles);

  if (!hasAccess) {
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default ProtectedRoute;